<script setup lang="ts">
import { BaseHeader, type BaseHeaderImage } from '@components/base';
import { computed } from 'vue';

type ListRow = { text?: string };
type SideMedia = { url: string; mediaType?: string };

const props = withDefaults(
  defineProps<{
    blockClass?: string;
    headingHtml?: string;
    headingObjects?: BaseHeaderImage[] | string;
    descriptionHtml?: string;
    listItems?: ListRow[] | string;
    heroCtaHref?: string;
    heroCtaTarget?: string;
    heroCtaText?: string;
    redText?: string;
    tips?: string;
    sideMedias?: SideMedia[] | string;
    sideBgColor?: string | null;
  }>(),
  {
    blockClass: '',
    headingHtml: '',
    headingObjects: () => [],
    descriptionHtml: '',
    listItems: () => [],
    heroCtaHref: '',
    heroCtaTarget: '_self',
    heroCtaText: '',
    redText: '',
    tips: '',
    sideMedias: () => [],
    sideBgColor: '',
  },
);

function parseHeadingObjects(raw: BaseHeaderImage[] | string | undefined): BaseHeaderImage[] {
  if (raw === undefined || raw === null) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    const s = raw.trim();
    if (s === '' || s === '[]') return [];
    try {
      const parsed = JSON.parse(s) as unknown;
      return Array.isArray(parsed) ? (parsed as BaseHeaderImage[]) : [];
    } catch {
      return [];
    }
  }
  return [];
}

function parseList(raw: ListRow[] | string | undefined): string[] {
  if (raw === undefined || raw === null) return [];
  if (Array.isArray(raw)) {
    return raw.map((r) => (r?.text ?? '').trim()).filter(Boolean);
  }
  if (typeof raw === 'string') {
    const s = raw.trim();
    if (s === '' || s === '[]') return [];
    try {
      const parsed = JSON.parse(s) as unknown;
      if (!Array.isArray(parsed)) return [];
      return (parsed as ListRow[]).map((r) => (typeof r === 'object' && r && 'text' in r ? String((r as ListRow).text ?? '') : '').trim()).filter(Boolean);
    } catch {
      return [];
    }
  }
  return [];
}

function parseSideMedias(raw: SideMedia[] | string | undefined): SideMedia[] {
  if (raw === undefined || raw === null) return [];
  if (Array.isArray(raw)) return raw.filter((m) => (m?.url ?? '').trim().length > 0);
  if (typeof raw === 'string') {
    const s = raw.trim();
    if (s === '' || s === '[]') return [];
    try {
      const parsed = JSON.parse(s) as unknown;
      return Array.isArray(parsed) ? (parsed as SideMedia[]) : [];
    } catch {
      return [];
    }
  }
  return [];
}

const headingObjectsList = computed(() => parseHeadingObjects(props.headingObjects));
const listLines = computed(() => parseList(props.listItems));
const gallery = computed(() => parseSideMedias(props.sideMedias));

const headingContentClass = 'font-inter-tight text-34 sm:text-40 lg:text-56 font-semibold leading-[1.05] tracking-[-0.01em] max-w-[760px] [&_p]:m-0';

const hasImage = computed(() => gallery.value.length > 0);

const sidePanelStyle = computed(() => {
  const bg = props.sideBgColor?.trim();
  if (!bg) return undefined;
  return { backgroundColor: bg };
});

const showFooterBlock = computed(() => listLines.value.length > 0
  || (props.heroCtaHref ?? '').trim().length > 0
  || (props.redText ?? '').trim().length > 0
  || (props.tips ?? '').trim().length > 0);

const show = computed(() => {
  const h = (props.headingHtml ?? '').replace(/<[^>]*>/g, '').trim();
  const d = (props.descriptionHtml ?? '').replace(/<[^>]*>/g, '').trim();
  return (
    h.length > 0
    || d.length > 0
    || listLines.value.length > 0
    || (props.heroCtaHref ?? '').trim().length > 0
    || gallery.value.length > 0
    || (props.redText ?? '').trim().length > 0
    || (props.tips ?? '').trim().length > 0
  );
});
</script>

<template>
  <section
    v-if="show"
    :class="['pricing-plans', blockClass, 'my-40 pb-20 pt-14 md:my-56 md:pb-24 md:pt-20 lg:my-64 lg:pb-36 lg:pt-28']"
  >
    <div class="container">
      <div class="grid items-start gap-20 md:gap-24 lg:grid-cols-2 lg:gap-32 xl:gap-40">
        <div
          class="px-20 text-deep-blue md:px-0"
          :class="{ 'lg:max-w-[700px]': !hasImage }"
        >
          <div class="flex flex-col">
            <BaseHeader
              v-if="headingHtml"
              v-cloak
              as="h2"
              :unwrap-outer-paragraph="true"
              :content="headingHtml"
              :objects="headingObjectsList"
              :content-class="headingContentClass"
            />

            <p
              v-if="descriptionHtml"
              class="mt-18 max-w-[720px] text-16 font-medium leading-tight lg:text-18"
              v-html="descriptionHtml"
            />

            <div
              v-if="hasImage"
              class="relative mt-20 w-full lg:hidden"
            >
              <div
                class="relative w-full overflow-hidden rounded-[24px] pt-[92%] sm:pt-[80%]"
                :style="sidePanelStyle"
              >
                <template
                  v-for="(m, idx) in gallery"
                  :key="`m-${idx}-${m.url}`"
                >
                  <video
                    v-if="m.mediaType === 'video'"
                    class="absolute inset-0 size-full rounded-[24px] object-cover"
                    :src="m.url"
                    playsinline
                    controls
                    autoplay
                    muted
                  />
                  <img
                    v-else
                    :src="m.url"
                    alt=""
                    class="absolute inset-0 size-full rounded-[24px] object-cover"
                    loading="lazy"
                    decoding="async"
                  >
                </template>
              </div>
            </div>

            <div
              v-if="showFooterBlock"
              class="my-16 max-w-[720px] lg:my-64"
            >
              <ul
                v-if="listLines.length"
                class="space-y-12 text-15 leading-120 lg:space-y-10 lg:text-18"
              >
                <li
                  v-for="(line, i) in listLines"
                  :key="`${i}-${line}`"
                  class="flex items-start gap-8"
                >
                  <span class="mt-1 shrink-0 text-copper-500">
                    <svg
                      class="size-18 lg:size-20"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M2 12C2 17.5228 6.47715 22 12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12ZM4 12C4 7.58172 7.58172 4 12 4C16.4183 4 20 7.58172 20 12C20 16.4183 16.4183 20 12 20C7.58172 20 4 16.4183 4 12ZM16.7372 9.67572C17.1103 9.26861 17.0828 8.63604 16.6757 8.26285C16.2686 7.88965 15.636 7.91716 15.2628 8.32428L10.5359 13.481L8.76822 11.3598C8.41466 10.9355 7.78409 10.8782 7.35982 11.2318C6.93554 11.5853 6.87821 12.2159 7.23178 12.6402L9.73178 15.6402C9.91649 15.8618 10.1879 15.9929 10.4764 15.9997C10.7648 16.0065 11.0422 15.8884 11.2372 15.6757L16.7372 9.67572Z"
                        fill="currentColor"
                      />
                    </svg>
                  </span>
                  <span>{{ line }}</span>
                </li>
              </ul>

              <div
                v-if="heroCtaHref"
                :class="{ 'mt-24 lg:mb-16 lg:mt-64': listLines.length > 0 }"
              >
                <a
                  :href="heroCtaHref"
                  :target="heroCtaTarget || '_self'"
                  class="mx-auto flex w-full max-w-full items-center justify-center rounded-full bg-off-black-500 px-28 py-14 font-inter-tight text-14 font-semibold tracking-wide text-white transition-opacity hover:opacity-90 lg:mx-0 lg:px-32 lg:py-16 lg:text-16 xl:w-fit"
                >
                  <span v-html="heroCtaText" />
                </a>
              </div>

              <p
                v-if="redText"
                class="mt-14 font-bold text-red"
              >
                {{ redText }}
              </p>
              <p
                v-if="tips"
                class="mt-14 text-11 text-deep-blue/70"
              >
                {{ tips }}
              </p>
            </div>
          </div>
        </div>

        <div
          v-if="hasImage"
          class="relative mx-auto hidden w-full max-w-[620px] px-20 md:px-0 lg:mx-0 lg:block lg:justify-self-end"
        >
          <div
            class="relative w-full overflow-hidden rounded-[28px] pt-[70%]"
            :style="sidePanelStyle"
          >
            <template
              v-for="(m, idx) in gallery"
              :key="`d-${idx}-${m.url}`"
            >
              <video
                v-if="m.mediaType === 'video'"
                class="absolute inset-0 size-full rounded-[24px] object-cover lg:rounded-[28px]"
                :src="m.url"
                playsinline
                controls
                autoplay
                muted
              />
              <img
                v-else
                :src="m.url"
                alt=""
                class="absolute inset-0 size-full rounded-[24px] object-cover lg:rounded-[28px]"
                loading="lazy"
                decoding="async"
              >
            </template>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
